// Shared helpers for the ad campaign screens: talking to /api/ads and turning
// Meta's campaign objects into the design's vocabulary (state/marketing.tsx).
import axios from "axios";
import config from "../../config";

/** Auth headers, plus the Meta user token the Marketing API needs when the clinic has one. */
export function metaHeaders() {
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const userToken = localStorage.getItem("meta_user_token_temp");
  if (userToken) headers["X-Meta-User-Token"] = userToken;
  return { headers };
}
export const apiGet = (path) => axios.get(`${config.API_BASE_URL}${path}`, metaHeaders()).then((r) => r.data).catch(() => null);

// Meta objective → the design's goals
const OBJECTIVE = {
  OUTCOME_LEADS: "Lead form", LEAD_GENERATION: "Lead form",
  OUTCOME_ENGAGEMENT: "WhatsApp chats", MESSAGES: "WhatsApp chats",
  OUTCOME_TRAFFIC: "Website visits", LINK_CLICKS: "Website visits",
  OUTCOME_AWARENESS: "Awareness", REACH: "Awareness", BRAND_AWARENESS: "Awareness",
};
export const objectiveOf = (o) => OBJECTIVE[o] || (o ? o.replace(/^OUTCOME_/, "").toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "");

export const RESULT_NOUN = {
  "Lead form": "leads",
  "WhatsApp chats": "chats",
  "Phone calls": "calls",
  "Website visits": "visits",
  Awareness: "people reached",
};
export const resultNoun = (objective) => RESULT_NOUN[objective] || "results";

// Meta effective_status → the design's campaign statuses
const IN_REVIEW = new Set(["IN_PROCESS", "PENDING_REVIEW", "WITH_ISSUES", "PREAPPROVED"]);
export function statusOf(c) {
  const s = c.effective_status || c.status;
  if (s === "ACTIVE") return "Active";
  if (s === "PAUSED" || s === "CAMPAIGN_PAUSED" || s === "ADSET_PAUSED") return "Paused";
  if (IN_REVIEW.has(s)) return "In review";
  if (!s || s === "DRAFT") return "Draft";
  return "Paused";
}

/** Daily budget in rupees. The cached list stores micros; Meta's own objects use the currency's minor unit (paise). */
export function dailyBudgetOf(c) {
  if (c.daily_budget_micros) return Number(c.daily_budget_micros) / 1_000_000;
  if (c.daily_budget) return Number(c.daily_budget) / 100;
  return null;
}

const LEAD_ACTIONS = ["lead", "onsite_conversion.lead_grouped", "offsite_conversion.fb_pixel_lead"];
const CHAT_ACTIONS = ["onsite_conversion.messaging_conversation_started_7d"];
const CALL_ACTIONS = ["click_to_call_native_call_placed", "click_to_call_call_confirm"];
const sumActions = (actions, types) => (Array.isArray(actions) ? actions.filter((a) => types.includes(a.action_type)).reduce((s, a) => s + Number(a.value || 0), 0) : 0);

/** Meta's insights preset for a number of days (7 or 30). */
export const presetFor = (days) => (Number(days) <= 7 ? "last_7d" : "last_30d");

/** The ad account as an insights object id ("act_…"). */
export const adAccountObject = (id) => (id ? `act_${String(id).replace(/^act_/, "")}` : null);

/** Spend, results and cost per result for one campaign over a preset (GET /api/ads/insights/:campaignId). */
export async function campaignTotals(campaignId, objective, preset) {
  const r = await apiGet(`/api/ads/insights/${campaignId}?preset=${preset}`);
  const s = r?.success ? r.data?.summary : null;
  if (!s) return { spend: null, results: null, costPerResult: null, daily: [] };
  const spend = s.spend != null ? Number(s.spend) : null;
  const results = resultsFrom(s, objective);
  return { spend, results, costPerResult: results && spend !== null ? spend / results : null, daily: r.data.daily || [] };
}

/** The campaign's results for its goal, from an insights row (summary or one day). */
export function resultsFrom(row, objective) {
  if (!row) return null;
  if (objective === "Lead form") return sumActions(row.actions, LEAD_ACTIONS);
  if (objective === "WhatsApp chats") return sumActions(row.actions, CHAT_ACTIONS);
  if (objective === "Phone calls") return sumActions(row.actions, CALL_ACTIONS);
  if (objective === "Website visits") return sumActions(row.actions, ["link_click"]) || (row.clicks != null ? Number(row.clicks) : null);
  if (objective === "Awareness") return row.reach != null ? Number(row.reach) : null;
  return sumActions(row.actions, LEAD_ACTIONS);
}
